<template>
  <div
    v-if="isVisible"
    class="fixed z-50 bottom-4 right-4 w-[20rem] rounded-lg shadow-lg border bg-white border-slate-100 dark:bg-slate-800 dark:border-slate-700"
  >
    <!-- Registration failed: the SDK cannot report this itself, so it is
         surfaced prominently rather than leaving a silently dead phone. -->
    <div v-if="hasRegistrationFailed" class="p-4">
      <div class="flex items-start gap-2">
        <fluent-icon
          icon="warning"
          size="16"
          class="mt-0.5 shrink-0 text-red-500"
        />
        <div class="flex-1">
          <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
            Web calling unavailable
          </p>
          <p class="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {{
              errorMessage ||
              'Your softphone could not connect. Check your network, then retry.'
            }}
          </p>
        </div>
      </div>
      <woot-button
        size="small"
        variant="clear"
        class="mt-2"
        @click="$emit('retry')"
      >
        Retry
      </woot-button>
    </div>

    <!-- Another tab already owns the softphone. -->
    <div v-else-if="!hasPhoneLease" class="flex items-center gap-2 p-4">
      <fluent-icon icon="info" size="16" class="shrink-0 text-slate-400" />
      <p class="text-xs leading-5 text-slate-500 dark:text-slate-400">
        Calls are active in another Chatwoot tab.
      </p>
    </div>

    <template v-else>
      <div
        class="flex items-center justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-700"
      >
        <div class="flex items-center gap-2">
          <span
            class="w-2 h-2 rounded-full"
            :class="isRegistered ? 'bg-green-500' : 'bg-slate-300'"
          />
          <span class="text-xs font-medium text-slate-600 dark:text-slate-300">
            {{ statusLabel }}
          </span>
        </div>
        <span
          v-if="isOnCall"
          class="text-xs tabular-nums text-slate-500 dark:text-slate-400"
        >
          {{ formattedDuration }}
        </span>
        <!-- The agent's own switch. Hidden mid-call: unregistering does not
             end a call, and offering it there only invites the confusion. -->
        <woot-button
          v-else
          size="tiny"
          variant="clear"
          :color-scheme="isAvailable ? 'secondary' : 'warning'"
          :title="availabilityHint"
          @click="onToggleAvailability"
        >
          {{ isAvailable ? 'Stop calls' : 'Take calls' }}
        </woot-button>
      </div>

      <div v-if="isOnCall" class="p-4">
        <div class="flex items-center gap-2 min-w-0">
          <thumbnail
            v-if="callerContact"
            :src="callerContact.thumbnail"
            :username="callerContact.name"
            size="32px"
          />
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 min-w-0">
              <p
                class="text-sm font-medium truncate text-slate-800 dark:text-slate-100"
              >
                {{ displayName }}
              </p>
              <a
                v-if="callerContact"
                :href="contactProfileLink"
                target="_blank"
                rel="noopener nofollow noreferrer"
                title="Open contact"
                class="shrink-0"
              >
                <woot-button
                  size="tiny"
                  icon="open"
                  variant="clear"
                  color-scheme="secondary"
                />
              </a>
            </div>
            <p
              v-if="callerContact"
              class="text-xs truncate text-slate-500 dark:text-slate-400"
            >
              {{ activeCall.remoteNumber }}
            </p>
          </div>
        </div>
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ callStateLabel }}
        </p>

        <!-- Incoming: accept or reject. -->
        <div v-if="isIncoming" class="flex gap-2 mt-4">
          <woot-button
            size="small"
            color-scheme="success"
            icon="call-inbound"
            @click="onAccept"
          >
            Accept
          </woot-button>
          <woot-button
            size="small"
            color-scheme="alert"
            icon="call-end"
            @click="onHangup"
          >
            Reject
          </woot-button>
        </div>

        <!-- In progress: mute, hold, hang up. -->
        <div v-else class="flex gap-2 mt-4">
          <woot-button
            size="small"
            variant="smooth"
            :color-scheme="isMuted ? 'warning' : 'secondary'"
            :disabled="!isConnected"
            @click="onToggleMute"
          >
            {{ isMuted ? 'Unmute' : 'Mute' }}
          </woot-button>
          <woot-button
            size="small"
            variant="smooth"
            :color-scheme="isOnHold ? 'warning' : 'secondary'"
            :disabled="!isConnected"
            @click="onToggleHold"
          >
            {{ isOnHold ? 'Resume' : 'Hold' }}
          </woot-button>
          <woot-button
            size="small"
            color-scheme="alert"
            icon="call-end"
            @click="onHangup"
          >
            End
          </woot-button>
        </div>
      </div>

      <div v-else class="p-4">
        <p class="text-xs leading-5 text-slate-500 dark:text-slate-400">
          {{ idleLabel }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import {
  REGISTRATION_STATES,
  CALL_STATES,
  CALL_DIRECTIONS,
} from 'dashboard/constants/calling';

export default {
  name: 'CallWidget',

  components: { Thumbnail },

  data() {
    return {
      elapsedSeconds: 0,
      durationTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      registrationState: 'calling/getRegistrationState',
      callState: 'calling/getCallState',
      activeCall: 'calling/getActiveCall',
      isRegistered: 'calling/isRegistered',
      isOnCall: 'calling/isOnCall',
      isMuted: 'calling/isMuted',
      isOnHold: 'calling/isOnHold',
      hasPhoneLease: 'calling/hasPhoneLease',
      errorMessage: 'calling/getErrorMessage',
      isProvisioned: 'calling/isProvisioned',
      isAvailable: 'calling/isAvailable',
      currentAccountId: 'getCurrentAccountId',
    }),

    availabilityHint() {
      return this.isAvailable
        ? 'Stop receiving calls in this browser'
        : 'Start receiving calls again';
    },

    idleLabel() {
      if (!this.isAvailable)
        return 'Not taking calls. Calls will not ring here.';
      return this.isRegistered
        ? 'Ready to take calls.'
        : 'Connecting your softphone…';
    },

    callerContact() {
      return this.activeCall?.contact || null;
    },

    contactProfileLink() {
      if (!this.callerContact) return '';
      return `/app/accounts/${this.currentAccountId}/contacts/${this.callerContact.id}`;
    },

    hasRegistrationFailed() {
      return this.registrationState === REGISTRATION_STATES.FAILED;
    },

    // The widget stays out of the way until it has something to say: a live
    // call, a failure, a tab conflict — or an agent who has signed off, who
    // would otherwise have no way back on.
    isVisible() {
      if (!this.isProvisioned) return false;
      return (
        this.isOnCall ||
        this.hasRegistrationFailed ||
        !this.hasPhoneLease ||
        !this.isAvailable ||
        this.registrationState !== REGISTRATION_STATES.UNREGISTERED
      );
    },

    isIncoming() {
      return this.callState === CALL_STATES.INCOMING;
    },

    isConnected() {
      return this.callState === CALL_STATES.CONNECTED;
    },

    displayName() {
      const { remoteName, remoteNumber } = this.activeCall || {};
      return (
        this.callerContact?.name ||
        remoteName ||
        remoteNumber ||
        'Unknown number'
      );
    },

    statusLabel() {
      if (!this.isAvailable) return 'Not taking calls';
      if (this.isRegistered) return 'Web calling on';
      if (this.registrationState === REGISTRATION_STATES.REGISTERING)
        return 'Connecting…';
      return 'Web calling off';
    },

    callStateLabel() {
      const isOutbound =
        this.activeCall?.direction === CALL_DIRECTIONS.OUTBOUND;
      if (this.isIncoming) return 'Incoming call';
      if (this.callState === CALL_STATES.OUTGOING) return 'Connecting…';
      if (this.isOnHold) return 'On hold';
      // The browser only sees its own leg; for an outbound call the customer
      // may still be ringing after we are "connected".
      return isOutbound ? 'Calling customer' : 'Inbound call';
    },

    formattedDuration() {
      const minutes = Math.floor(this.elapsedSeconds / 60);
      const seconds = this.elapsedSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`;
    },
  },

  watch: {
    // The SDK reports duration only alongside events, so the ticking clock the
    // agent sees is kept locally and reset whenever a call starts or ends.
    callState(newState, oldState) {
      if (newState === CALL_STATES.CONNECTED) {
        this.startDurationTimer();
      } else if (
        newState === CALL_STATES.IDLE ||
        oldState === CALL_STATES.IDLE
      ) {
        this.stopDurationTimer();
      }
    },
  },

  beforeDestroy() {
    this.stopDurationTimer();
  },

  methods: {
    startDurationTimer() {
      this.stopDurationTimer();
      this.elapsedSeconds = 0;
      this.durationTimer = setInterval(() => {
        this.elapsedSeconds += 1;
      }, 1000);
    },

    stopDurationTimer() {
      if (this.durationTimer) clearInterval(this.durationTimer);
      this.durationTimer = null;
      this.elapsedSeconds = 0;
    },

    onAccept() {
      this.$store.dispatch('calling/acceptCall');
    },

    onHangup() {
      this.$store.dispatch('calling/hangupCall');
    },

    onToggleMute() {
      this.$store.dispatch('calling/toggleMute');
    },

    onToggleHold() {
      this.$store.dispatch('calling/toggleHold');
    },

    onToggleAvailability() {
      this.$store.dispatch('calling/setAvailability', !this.isAvailable);
    },
  },
};
</script>
